import { Outlet, useLocation, useNavigate } from "react-router";
import { NavigationBarMenu } from "../../components/navigation/NavigationBar";
import { Icon } from "../../components";
import { useMediaQuery } from "usehooks-ts";
import { twMerge } from "tailwind-merge";

import * as ROUTES from "../../constants/routes";
import { StorefrontPreviewer } from "../../components/storefrontPreviewer/StorefrontPreviewer";
import {
  StorefrontPreviewerTabs,
  useStorefrontPreviewerParameters,
} from "../../components/storefrontPreviewer/StorefrontPreviewer.shared";
import { useFeatureFlagStore } from "../../hooks/useFeatureFlags";
import { Anchor, Button, H4 } from "@sellar-io/components";
import { useRemoteFeatureFlags } from "../../hooks/useRemoteFeatureFlags";

export const PriceLists = () => {
  const isDesktop = useMediaQuery("(min-width: 1200px)");
  const isSupplierStorefrontFeatureFlagEnabled = useFeatureFlagStore().isActive(
    "SUPPLIER_STOREFRONT_PREVIEW",
  );
  const remoteFeatureFlags = useRemoteFeatureFlags();
  const unbundling2bEnabled = remoteFeatureFlags.isActive(
    "unbundlingRelease2B",
  );
  const unbundling4aEnabled = remoteFeatureFlags.isActive(
    "unbundlingReleaseFinal",
  );

  const supplierOrderPromotionsEnabled = remoteFeatureFlags.isActive(
    "supplierOrderPromotions",
  );

  return (
    <>
      <NavigationBarMenu
        title="Pricing"
        borderBottom={false}
        rightComponent={
          isSupplierStorefrontFeatureFlagEnabled ? (
            <StorefrontPreviewButton />
          ) : null
        }
      />
      <div className="mx-auto flex max-w-230 items-center gap-2">
        <div
          className={twMerge(
            "mb-24 flex w-full flex-col gap-4 px-4",
            isDesktop ? "mt-10" : "mt-2",
          )}
        >
          <div className="flex flex-col gap-1 border-b border-zinc-300 pb-4">
            <H4 className="font-semibold">Pricing</H4>
            <p>
              Set up your pricing structures. For more information or help
              setting up your pricing, visit our{" "}
              <Anchor
                className="inline-flex items-center gap-1 text-blue-600 underline"
                href="https://intercom.help/sellario/en/articles/10148283-supplier-setup-pricing-and-discounts"
                target="_blank"
                rel="noopener noreferrer"
              >
                help centre
                <Icon icon="ARROW_TOP_RIGHT" size={0.6} />
              </Anchor>
              .
            </p>
          </div>

          {unbundling2bEnabled && (
            <Tabs
              showVolumeDiscounts={unbundling4aEnabled}
              showPromotions={supplierOrderPromotionsEnabled}
            />
          )}
          <Outlet />
        </div>
      </div>
    </>
  );
};

const baseTabs = [
  {
    label: "Price Lists",
    route: ROUTES.PRICE_LISTS,
  },
];

const volumeDiscountsTab = {
  label: "Volume Discounts",
  route: ROUTES.VOLUME_DISCOUNTS,
};

const promotionsTab = {
  label: "Promotions",
  route: ROUTES.PROMOTIONS,
};

export const Tabs = ({
  showVolumeDiscounts,
  showPromotions,
}: {
  showVolumeDiscounts: boolean;
  showPromotions: boolean;
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  if (!showVolumeDiscounts && !showPromotions) {
    return null;
  }

  let tabs = showVolumeDiscounts ? [...baseTabs, volumeDiscountsTab] : baseTabs;
  tabs = showPromotions ? [...tabs, promotionsTab] : tabs;

  return (
    <div className="mb-4 flex w-full items-center border-b border-zinc-300">
      {tabs.map((tab) => (
        <div
          key={tab.route}
          onClick={() => navigate(tab.route)}
          className={twMerge(
            "max-w-42 grow cursor-pointer border-b-4 border-transparent px-2 py-4 text-center font-semibold text-zinc-500 hover:bg-zinc-100",
            location.pathname === tab.route && "border-zinc-800 text-zinc-800",
          )}
        >
          <span>{tab.label}</span>
        </div>
      ))}
    </div>
  );
};

const StorefrontPreviewButton = () => {
  const widthLessThan640 = useMediaQuery("(max-width: 640px)");

  const {
    openStorefrontPreviewer,
    businessName,
    featureFlags,
    customerZones,
    isSearchingForCustomer,
    isSearchingForCustomerZone,
    customers,
    orderRule,
    selectedCustomer,
    productListQuery,
    tab,
    selectedCustomerZone,
    setSelectedCustomerZone,
    setOpenStorefrontPreviewer,
    setSelectedCustomer,
    setCustomerSearchTerm,
    setCustomerZoneSearchTerm,
    onChangeTab,
    onPreviewStorefront,
  } = useStorefrontPreviewerParameters({
    initialTab: StorefrontPreviewerTabs.CUSTOMER,
  });

  return (
    <StorefrontPreviewer
      open={openStorefrontPreviewer}
      initialTab={tab}
      trigger={
        <Button
          onClick={() => setOpenStorefrontPreviewer(true)}
          color="secondary"
          className="p-2"
        >
          <Icon icon="STOREFRONT_OUTLINE" />
          {!widthLessThan640 && <span>Preview</span>}
        </Button>
      }
      businessName={businessName}
      featureFlags={featureFlags}
      customerZoneSearchProps={{
        customerZones,
        isSearchingForCustomerZone,
        selectedCustomerZone,
        onSelectCustomerZone: setSelectedCustomerZone,
        onSearchForCustomerZone: setCustomerZoneSearchTerm,
      }}
      customerSearchProps={{
        customers,
        isSearchingForCustomer,
        selectedCustomer,
        onSelectCustomer: setSelectedCustomer,
        onSearchForCustomer: setCustomerSearchTerm,
      }}
      onTogglePreviewer={setOpenStorefrontPreviewer}
      orderRule={{
        name: orderRule?.name ?? "",
        minimumOrderValue: orderRule?.minimumOrderPrice ?? 0,
        minimumOrderQuantities: orderRule?.PriceTierOrderRules ?? [],
      }}
      productListQuery={productListQuery}
      onClickStorefrontPreview={onPreviewStorefront}
      onChangeTab={onChangeTab}
    />
  );
};
